<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Archive, Check, ChevronLeft, ChevronRight, Undo2, X } from "lucide-vue-next";
  import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";
  import { useMAssetLibraryStore } from "@stores/mAssetLibrary";
  import ImageLibraryAssetThumbnail from "@/components/MCampaign/ImageLibrary/ImageLibraryAssetThumbnail.vue";
  import ImageLibraryAssetDetails from "@/components/MCampaign/ImageLibrary/ImageLibraryAssetDetails.vue";

  const assetLibraryStore = useMAssetLibraryStore();
  const { lightboxAsset, lightboxIndex, totalCount, membership, allAssetTags, selectedIds } =
    storeToRefs(assetLibraryStore);
  const {
    closeLightbox,
    stepLightbox,
    takeLightboxAssetOut,
    hideFromLibrary,
    restoreToLibrary,
    toggleSelection,
    updateAsset,
    recordDimensions,
    openLightbox,
  } = assetLibraryStore;

  const isOpen = computed(() => !!lightboxAsset.value);
  const isSelected = computed(() => !!lightboxAsset.value && selectedIds.value.includes(lightboxAsset.value.id));

  const tagOptions = computed(() => allAssetTags.value.map((tag) => ({ value: tag.name, label: tag.name })));
  const tags = ref<string[]>([]);
  watch(
    () => lightboxAsset.value?.id,
    () => (tags.value = [...(lightboxAsset.value?.asset_tags ?? [])]),
    { immediate: true },
  );

  const saveTags = async (next: string[]) => {
    tags.value = next;
    if (!lightboxAsset.value || !next.length) return;
    await updateAsset({ ...lightboxAsset.value, asset_tags: next });
  };

  // A toast would sit under the modal lightbox and stay unclickable, so the undo lives in the panel.
  const lastMove = ref<{ id: number; name: string; wasOut: boolean } | null>(null);

  const moveOut = async () => {
    const wasOut = membership.value === "out";
    const moved = await takeLightboxAssetOut(wasOut ? restoreToLibrary : hideFromLibrary);
    if (moved) lastMove.value = { id: moved.id, name: moved.name, wasOut };
  };

  const undoLastMove = async () => {
    const move = lastMove.value;
    if (!move) return;
    lastMove.value = null;
    const succeeded = await (move.wasOut ? hideFromLibrary([move.id]) : restoreToLibrary([move.id]));
    if (succeeded) openLightbox(move.id);
  };

  watch(isOpen, (open) => !open && (lastMove.value = null));

  const tagsField = ref<HTMLElement | null>(null);
  const focusTags = () => tagsField.value?.querySelector<HTMLElement>("button")?.click();

  const isTyping = (event: KeyboardEvent) => {
    const target = event.target;
    return target instanceof Element && !!target.closest("input, textarea, [contenteditable='true'], [role='listbox']");
  };

  const shortcuts: Record<string, () => unknown> = {
    ArrowLeft: () => stepLightbox(-1),
    ArrowRight: () => stepLightbox(1),
    " ": () => lightboxAsset.value && toggleSelection(lightboxAsset.value.id),
    x: moveOut,
    z: undoLastMove,
    t: focusTags,
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (!isOpen.value || isTyping(event) || event.metaKey || event.ctrlKey || event.altKey) return;
    const shortcut = shortcuts[event.key.length === 1 ? event.key.toLowerCase() : event.key];
    if (!shortcut) return;
    event.preventDefault();
    shortcut();
  };

  watch(isOpen, (open) => {
    if (open) window.addEventListener("keydown", onKeydown);
    else window.removeEventListener("keydown", onKeydown);
  });

  onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));
</script>

<template>
  <Dialog
    :open="isOpen"
    @update:open="(open) => !open && closeLightbox()">
    <DialogContent
      :show-close-button="false"
      class="flex h-[calc(100vh-40px)] max-w-[calc(100vw-60px)] gap-0 overflow-hidden p-0">
      <div class="relative flex flex-1 items-center justify-center bg-black">
        <ImageLibraryAssetThumbnail
          v-if="lightboxAsset"
          :key="lightboxAsset.id"
          :asset="lightboxAsset"
          fit="contain"
          playable
          @measured="(dimensions) => lightboxAsset && recordDimensions(lightboxAsset.id, dimensions)" />

        <Button
          variant="secondary"
          size="icon"
          class="absolute left-4 top-1/2 -translate-y-1/2 rounded-full"
          aria-label="Previous asset"
          :disabled="lightboxIndex <= 0"
          @click="stepLightbox(-1)">
          <ChevronLeft class="h-5 w-5" />
        </Button>
        <Button
          variant="secondary"
          size="icon"
          class="absolute right-4 top-1/2 -translate-y-1/2 rounded-full"
          aria-label="Next asset"
          :disabled="lightboxIndex >= totalCount - 1"
          @click="stepLightbox(1)">
          <ChevronRight class="h-5 w-5" />
        </Button>
        <span class="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
          {{ lightboxIndex + 1 }} / {{ totalCount }}
        </span>
      </div>

      <aside
        v-if="lightboxAsset"
        class="flex w-80 shrink-0 flex-col border-l bg-background">
        <div class="flex items-start justify-between gap-2 border-b px-4 py-3">
          <div class="min-w-0">
            <DialogTitle class="heading-section truncate">{{ lightboxAsset.name }}</DialogTitle>
            <DialogDescription class="text-xs text-muted-foreground">
              Used in {{ lightboxAsset.used_in_creatives_count ?? 0 }} creative(s)
            </DialogDescription>
          </div>
          <DialogClose as-child>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close">
              <X class="h-4 w-4" />
            </Button>
          </DialogClose>
        </div>

        <div
          v-if="lastMove"
          class="flex items-center justify-between gap-2 border-b bg-muted px-4 py-2 text-xs">
          <span class="truncate">
            “{{ lastMove.name }}” {{ lastMove.wasOut ? "is back in the library" : "removed from the library" }}
          </span>
          <Button
            variant="link"
            size="sm"
            class="h-auto shrink-0 p-0 text-xs"
            @click="undoLastMove">
            Undo (Z)
          </Button>
        </div>

        <div class="flex-1 space-y-4 overflow-y-auto p-4">
          <div
            ref="tagsField"
            class="space-y-1">
            <label class="text-xs font-medium text-muted-foreground">Tags</label>
            <Select
              :model-value="tags"
              :options="tagOptions"
              multiple
              filterable
              creatable
              placeholder="Add tags"
              @update:model-value="(value) => saveTags(value as string[])" />
            <p
              v-if="!tags.length"
              class="text-xs text-status-warning-strong">
              Keep at least one tag: an untagged asset cannot be found by search.
            </p>
          </div>

          <ImageLibraryAssetDetails :asset="lightboxAsset" />

          <div class="flex flex-col gap-2">
            <Button
              :variant="isSelected ? 'secondary' : 'outline'"
              @click="toggleSelection(lightboxAsset.id)">
              <Check class="mr-1 h-4 w-4" />
              {{ isSelected ? "Selected" : "Select" }}
            </Button>
            <Button
              variant="outline"
              :class="membership === 'out' ? '' : 'text-status-error-strong'"
              @click="moveOut">
              <component
                :is="membership === 'out' ? Undo2 : Archive"
                class="mr-1 h-4 w-4" />
              {{ membership === "out" ? "Put back in library" : "Remove from library" }}
            </Button>
          </div>
        </div>

        <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 border-t px-4 py-3 text-xs text-muted-foreground">
          <dt><kbd class="rounded border px-1">←</kbd> <kbd class="rounded border px-1">→</kbd></dt>
          <dd>Previous / next</dd>
          <dt><kbd class="rounded border px-1">T</kbd></dt>
          <dd>Edit tags</dd>
          <dt><kbd class="rounded border px-1">Space</kbd></dt>
          <dd>Select</dd>
          <dt><kbd class="rounded border px-1">X</kbd></dt>
          <dd>{{ membership === "out" ? "Put back in library" : "Remove from library" }}</dd>
          <dt><kbd class="rounded border px-1">Z</kbd></dt>
          <dd>Undo the last removal</dd>
        </dl>
      </aside>
    </DialogContent>
  </Dialog>
</template>
